import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Container, Typography, TextField, Button, Paper,
    IconButton, Alert, CircularProgress,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    EventNote as EventIcon,
    LocationOn as LocationIcon,
    AccessTime as TimeIcon,
    CalendarMonth as CalendarIcon,
    ArrowBack as BackIcon,
} from '@mui/icons-material';
import { parseISO, format } from 'date-fns';
import { events } from '../services/api';
import { useAuth } from '../context/AuthContext';

// ── Helpers ───────────────────────────────────────────────────────────────────
const toLocalISO = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

// ── Hooks ─────────────────────────────────────────────────────────────────────
const useScrollY = () => {
    const [scrollY, setScrollY] = useState(0);
    useEffect(() => {
        const fn = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', fn, { passive: true });
        return () => window.removeEventListener('scroll', fn);
    }, []);
    return scrollY;
};

const useInView = (threshold = 0.08) => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const obs = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) setInView(true); },
            { threshold }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
};

const FadeUp = ({ children, delay = 0 }) => {
    const [ref, inView] = useInView();
    return (
        <Box ref={ref} sx={{
            opacity:   inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(32px)',
            transition: `opacity 0.55s ease ${delay}s, transform 0.55s ease ${delay}s`,
            willChange: 'opacity, transform',
        }}>
            {children}
        </Box>
    );
};

// ── SectionCard ───────────────────────────────────────────────────────────────
const SectionCard = ({ icon, label, children, delay = 0 }) => (
    <FadeUp delay={delay}>
        <Paper elevation={0} variant="outlined" sx={{ borderRadius: 4, overflow: 'hidden', borderColor: 'rgba(0,0,0,0.1)' }}>
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#FAFAFA', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: '#EDE9FE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {React.cloneElement(icon, { sx: { fontSize: 17, color: '#7C3AED' } })}
                </Box>
                <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, color: '#374151' }}>
                    {label}
                </Typography>
            </Box>
            <Box sx={{ px: 3, py: 3, bgcolor: 'white' }}>
                {children}
            </Box>
        </Paper>
    </FadeUp>
);

// ── Component ─────────────────────────────────────────────────────────────────
const EditEvent = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const scrollY = useScrollY();

    const [loading,    setLoading]    = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error,      setError]      = useState('');
    const [eventData,  setEventData]  = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const response = await events.getById(id);
                const ev = response.data;

                if (ev.organizer?.id !== user?.id) {
                    setError('Only the organizer can edit this event.');
                    setLoading(false);
                    return;
                }

                setEventData({
                    title:         ev.title || '',
                    description:   ev.description || '',
                    location:      ev.location || '',
                    votingDeadline: ev.votingDeadline ? parseISO(ev.votingDeadline) : null,
                    timeSlots: ev.timeSlots?.length > 0
                        ? ev.timeSlots.map(s => ({
                            startTime: parseISO(s.proposedStartTime),
                            endTime:   parseISO(s.proposedEndTime),
                        }))
                        : [{ startTime: null, endTime: null }],
                });
            } catch {
                setError('Failed to load event.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id, user]);

    const handleChange = (e) =>
        setEventData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSlotChange = (index, field, value) => {
        setEventData(prev => {
            const slots = [...prev.timeSlots];
            const updated = { ...slots[index], [field]: value };
            if (field === 'startTime' && value && !slots[index].endTime) {
                const oneHrLater = new Date(value);
                oneHrLater.setHours(oneHrLater.getHours() + 1);
                updated.endTime = oneHrLater;
            }
            slots[index] = updated;
            return { ...prev, timeSlots: slots };
        });
    };

    const addSlot = () =>
        setEventData(prev => ({ ...prev, timeSlots: [...prev.timeSlots, { startTime: null, endTime: null }] }));

    const removeSlot = (index) =>
        setEventData(prev => ({ ...prev, timeSlots: prev.timeSlots.filter((_, i) => i !== index) }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!eventData.title.trim()) { setError('Event title is required.'); return; }
        const validSlots = eventData.timeSlots.filter(s => s.startTime && s.endTime);
        if (validSlots.length === 0) { setError('Add at least one time slot with both start and end times.'); return; }

        setSubmitting(true);
        try {
            await events.update(id, {
                title:          eventData.title.trim(),
                description:    eventData.description.trim(),
                location:       eventData.location.trim(),
                votingDeadline: toLocalISO(eventData.votingDeadline),
                proposedTimeSlots: validSlots.map(s => ({
                    proposedStartTime: toLocalISO(s.startTime),
                    proposedEndTime:   toLocalISO(s.endTime),
                })),
            });
            navigate(`/events/${id}`);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update event.');
        } finally {
            setSubmitting(false);
        }
    };

    // ── Loading / access denied ────────────────────────────────────────────────
    if (loading) return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: '#7C3AED' }} />
        </Box>
    );

    if (error && !eventData) return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
            <Box sx={{ background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)', py: 6 }}>
                <Container maxWidth="lg">
                    <Button onClick={() => navigate(-1)} startIcon={<BackIcon />}
                        sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, mb: 2, px: 0, '&:hover': { color: 'white', bgcolor: 'transparent' } }}>
                        Back
                    </Button>
                </Container>
            </Box>
            <Container maxWidth="lg" sx={{ pt: 4 }}>
                <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>
            </Container>
        </Box>
    );

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>

            {/* ── Header ───────────────────────────────────────────────────── */}
            <Box sx={{ background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)', py: 6, position: 'relative', overflow: 'clip' }}>
                <Box sx={{ position: 'absolute', top: -50, left: '-3%', width: 360, height: 360, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', transform: `translateY(${scrollY*0.3}px)`, willChange: 'transform', pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', top: -10, right: '4%',  width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', transform: `translateY(${scrollY*0.5}px)`, willChange: 'transform', pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -40, left: '50%', width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', transform: `translateY(${scrollY*0.65}px)`, willChange: 'transform', pointerEvents: 'none' }} />

                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, transform: `translateY(${scrollY*0.2}px)`, willChange: 'transform' }}>
                    <Button onClick={() => navigate(`/events/${id}`)} startIcon={<BackIcon />}
                        sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, mb: 2, px: 0, '&:hover': { color: 'white', bgcolor: 'transparent' } }}>
                        Back to Event
                    </Button>
                    <Typography variant="h4" fontWeight={800} sx={{ color: 'white', mb: 0.5 }}>
                        Edit Event
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        {eventData?.title || 'Update your event details'}
                    </Typography>
                </Container>
            </Box>

            {/* ── Form ─────────────────────────────────────────────────────── */}
            <Container maxWidth="lg" sx={{ pt: 5, pb: 10 }}>
                <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                    {/* Event Details */}
                    <SectionCard icon={<EventIcon />} label="Event Details" delay={0.05}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 0.75 }}>
                                    Title *
                                </Typography>
                                <TextField fullWidth required size="small"
                                    name="title"
                                    placeholder="Give your event a name"
                                    value={eventData.title}
                                    onChange={handleChange}
                                    inputProps={{ style: { fontSize: 18, fontWeight: 700 } }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 0.75 }}>
                                        Description
                                    </Typography>
                                    <TextField fullWidth multiline rows={3} size="small"
                                        name="description"
                                        placeholder="What's this event about? (optional)"
                                        value={eventData.description}
                                        onChange={handleChange}
                                    />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 0.75 }}>
                                        Location
                                    </Typography>
                                    <TextField fullWidth size="small"
                                        name="location"
                                        placeholder="Where is it? (optional)"
                                        value={eventData.location}
                                        onChange={handleChange}
                                        InputProps={{ startAdornment: <LocationIcon sx={{ fontSize: 18, color: 'text.disabled', mr: 1 }} /> }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </SectionCard>

                    {/* Voting Deadline */}
                    <SectionCard icon={<TimeIcon />} label="Voting Deadline" delay={0.1}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Optional — leave blank to keep voting open indefinitely.
                        </Typography>
                        <DateTimePicker
                            label="Voting deadline (optional)"
                            value={eventData.votingDeadline}
                            onChange={(value) => setEventData(prev => ({ ...prev, votingDeadline: value }))}
                            slotProps={{ textField: { size: 'small', sx: { width: '100%', maxWidth: 360 } } }}
                        />
                    </SectionCard>

                    {/* Time Slots */}
                    <SectionCard icon={<CalendarIcon />} label="Proposed Time Slots" delay={0.15}>
                        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2, fontSize: '0.82rem' }}>
                            Updating time slots will clear existing votes for this event.
                        </Alert>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {eventData.timeSlots.map((slot, i) => (
                                <Paper key={i} elevation={0} variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', borderColor: 'rgba(0,0,0,0.1)' }}>
                                    {/* Slot header */}
                                    <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#FAFAFA', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" fontWeight={700} sx={{ color: '#7C3AED', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                            Option {i + 1}
                                            {slot.startTime && (
                                                <Typography component="span" variant="caption" sx={{ ml: 1.5, color: 'text.secondary', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>
                                                    — {format(new Date(slot.startTime), 'EEE, MMM d · h:mm a')}
                                                    {slot.endTime && ` – ${format(new Date(slot.endTime), 'h:mm a')}`}
                                                </Typography>
                                            )}
                                        </Typography>
                                        {eventData.timeSlots.length > 1 && (
                                            <IconButton size="small" onClick={() => removeSlot(i)}
                                                sx={{ p: 0.5, '&:hover': { bgcolor: '#FEE2E2', color: '#DC2626' } }}>
                                                <DeleteIcon sx={{ fontSize: 17 }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                    {/* Pickers */}
                                    <Box sx={{ px: 2.5, py: 2.5, display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                                        <DateTimePicker
                                            label="Start time"
                                            value={slot.startTime}
                                            onChange={(value) => handleSlotChange(i, 'startTime', value)}
                                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                        />
                                        <DateTimePicker
                                            label="End time"
                                            value={slot.endTime}
                                            onChange={(value) => handleSlotChange(i, 'endTime', value)}
                                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                        />
                                    </Box>
                                </Paper>
                            ))}

                            <Button startIcon={<AddIcon />} onClick={addSlot} size="small"
                                sx={{ alignSelf: 'flex-start', fontWeight: 700, color: '#7C3AED', borderRadius: 99, px: 2, py: 0.8, border: '1.5px dashed #A78BFA', '&:hover': { bgcolor: '#EDE9FE', borderColor: '#7C3AED' } }}>
                                Add another time option
                            </Button>
                        </Box>
                    </SectionCard>

                    {/* Error */}
                    {error && (
                        <FadeUp>
                            <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>
                        </FadeUp>
                    )}

                    {/* Actions */}
                    <FadeUp delay={0.2}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button type="submit" variant="contained" size="large" disabled={submitting}
                                sx={{ fontWeight: 700, borderRadius: 2.5, px: 5, py: 1.5, fontSize: '1rem', background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)', boxShadow: '0 4px 14px rgba(124,58,237,0.3)', '&:hover': { boxShadow: '0 6px 20px rgba(124,58,237,0.45)' } }}>
                                {submitting ? 'Saving…' : 'Save Changes'}
                            </Button>
                            <Button variant="outlined" size="large" onClick={() => navigate(`/events/${id}`)}
                                sx={{ fontWeight: 700, borderRadius: 2.5, borderColor: 'rgba(0,0,0,0.2)', color: 'text.secondary', '&:hover': { borderColor: '#7C3AED', color: '#7C3AED' } }}>
                                Cancel
                            </Button>
                        </Box>
                    </FadeUp>

                </Box>
            </Container>
        </Box>
    );
};

export default EditEvent;
